import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/layout/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AuthService, NotificationService, UserService } from '@/services';
import { SkeletonModule } from 'primeng/skeleton';
import { AvatarModule } from 'primeng/avatar';
import { AppStateService } from '@/services/app-state.service';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AppUser } from '@/models';

@Component({
  selector: '[app-topbar]',
  standalone: true,
  imports: [
    AvatarModule,
    RouterModule,
    CommonModule,
    StyleClassModule,
    AppBreadcrumb,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    MenuModule,
    SkeletonModule,
    OverlayBadgeModule,
  ],
  providers: [AuthService],
  templateUrl: './app.topbar.html',
})
export class AppTopbar implements OnInit {
  @ViewChild('menubutton') menuButton!: ElementRef;
  @ViewChild('profileMenu') profileMenu!: Menu;

  public layoutService = inject(LayoutService);
  public authService = inject(AuthService);
  private _notificationService = inject(NotificationService);
  private _appStateService = inject(AppStateService);
  private _userService = inject(UserService);

  menuVisible = false;
  public isLoggedIn = false;
  public loginResolved = false;

  private readonly appUser = toSignal(this._userService.appUser$, {
    initialValue: {} as AppUser,
  });

  public readonly user = computed(() => this.appUser().user);
  public readonly notificationCount = this._notificationService.totalCount;
  public readonly accountInvitationCount =
    this._notificationService.accountInvitationCount;

  //the badge is visual only -- fold the count into the button's accessible name
  public readonly profileMenuLabel = computed(() => {
    const count = this.notificationCount();
    return count
      ? `Profile menu, ${count} notification${count === 1 ? '' : 's'}`
      : 'Profile menu';
  });

  public readonly userInitials = computed(() => {
    const user = this.user();
    return (
      (user?.firstName?.charAt(0) ?? '') + (user?.lastName?.charAt(0) ?? '')
    );
  });

  public readonly profileMenuItems = computed<MenuItem[]>(() => {
    const firstName = this.user()?.firstName;
    const invitationCount = this.accountInvitationCount();
    return [
      {
        label: firstName ? `Welcome ${firstName}` : 'Welcome',
        items: [
          {
            label: 'Account Invitations',
            icon: 'pi pi-envelope',
            routerLink: ['/invitations'],
            badge: invitationCount ? `${invitationCount}` : undefined,
          },
          { separator: true },
          {
            label: 'Sign out',
            icon: 'pi pi-sign-out',
            command: () => this.authService.logout(),
          },
        ],
      },
    ];
  });

  async ngOnInit() {
    this.isLoggedIn = await this.authService.isLoggedIn();
    this.loginResolved = true;
    if (this._appStateService.useDarkTheme) {
      this.toggleDarkMode(this._appStateService.useDarkTheme === 'true');
    }
  }

  toggleDarkMode(useDarkTheme?: boolean) {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: useDarkTheme !== undefined ? useDarkTheme : !state.darkTheme,
    }));
    this._appStateService.handleChangeUseDarkTheme(
      !!this.layoutService.isDarkTheme(),
    );
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  onConfigButtonClick() {
    this.layoutService.showConfigSidebar();
  }

  onLoginButtonClick() {
    this.authService.login();
  }
}
